import { Minus, Plus } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import OnboardingHint, { useOnboardingHint } from './OnboardingHint';
import type { DiagramType } from '../lib/diagramTypes';
import { midpoint, pinchZoomFactor } from '../lib/pinch';
import type { PreviewState, RenderError } from '../lib/render';
import type { SyntaxHint } from '../lib/syntaxHints';
import { createViewport, type Size, type ViewportState, type Point } from '../lib/viewport';

/**
 * SPEC.md §3, §9 — the preview pane: a sheet on a dot-grid desk.
 *
 * M2.3 (issue #6) wired `viewport.ts` into the sheet from M2.1: pointer-drag
 * pan, `Cmd/Ctrl + wheel` zoom anchored under the cursor, plain wheel pans,
 * and a zoom cluster. M2.4 (issue #7) adds Fit, `Cmd/Ctrl+Shift+F`, and
 * auto-fit on first render / container resize. M2.5 (issue #8) adds
 * two-finger pinch, reusing the same pointer surface pan already uses: one
 * active pointer pans, two pinch, via the `activePointersRef` map below.
 * M4.3 (issue #17) completes the error card's third part (§9): a docs
 * deep-link for the detected type, plus an alias-mismatch hint when the
 * pinned Mermaid version wants a different keyword than the one the user
 * wrote. Also names the detected type in the preview region's aria-label
 * (§11) instead of a generic string.
 */

const ZOOM_STEP = 1.2;

interface PreviewProps {
  state: PreviewState;
  svg: string | null;
  error: RenderError | null;
  /** The currently detected diagram type, or null when unrecognised (§4). */
  diagramType: DiagramType | null;
  /** True when detection matched via an alias rather than the entry's canonical keyword. */
  matchedViaAlias: boolean;
  /** An unclosed-quote line, computed independently of Mermaid's own message (syntaxHints.ts) — null when the source has none. */
  syntaxHint: SyntaxHint | null;
  /** The current buffer's identity (App.tsx's `bufferId` state) — changes only on a wholesale replacement (New, Recent, drag-drop, hash load), never on plain typing. Re-arms auto-fit so *that* diagram's first render fits too. */
  bufferId: string;
}

export default function Preview({ state, svg, error, diagramType, matchedViaAlias, syntaxHint, bufferId }: PreviewProps) {
  const deskRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<ReturnType<typeof createViewport> | null>(null);
  const activePointersRef = useRef<Map<number, Point>>(new Map());
  const [viewportState, setViewportState] = useState<ViewportState>({ scale: 1, x: 0, y: 0 });
  const interactive = svg !== null;
  const onboarding = useOnboardingHint(interactive);

  useEffect(() => {
    const viewport = createViewport();
    viewportRef.current = viewport;
    const unsubscribe = viewport.subscribe(setViewportState);

    return () => {
      unsubscribe();
      viewport.destroy();
      viewportRef.current = null;
    };
  }, []);

  // Re-arms the one-shot auto-fit guard whenever the buffer itself is
  // wholesale-replaced (SPEC.md §3: "Auto-fit on first render of a
  // diagram" — a diagram, not just the session's first one ever). A no-op
  // on initial mount beyond what the viewport already starts with (its
  // guard is already unset right after `createViewport()` above, which
  // this effect's own declaration order guarantees has already run).
  useEffect(() => {
    viewportRef.current?.resetFitGuard();
  }, [bufferId]);

  function centerAnchor(): Point {
    const rect = deskRef.current?.getBoundingClientRect();
    return rect ? { x: rect.width / 2, y: rect.height / 2 } : { x: 0, y: 0 };
  }

  /**
   * The sheet's *layout* box (offsetWidth/Height) — not its painted
   * getBoundingClientRect, which already reflects the current pan/zoom
   * transform and would make fit's own output feed back into its input. The
   * desk container carries no transform of its own, so clientWidth/Height
   * (its padding-inclusive layout size) is a plain, untransformed read.
   * Null whenever there's no diagram on screen to fit against.
   */
  const currentSizes = useCallback((): { content: Size; container: Size } | null => {
    const sheet = sheetRef.current;
    const desk = deskRef.current;
    if (!sheet || !desk) return null;
    return {
      content: { width: sheet.offsetWidth, height: sheet.offsetHeight },
      container: { width: desk.clientWidth, height: desk.clientHeight },
    };
  }, []);

  const fitNow = useCallback(() => {
    const sizes = currentSizes();
    if (sizes) viewportRef.current?.fit(sizes.content, sizes.container);
  }, [currentSizes]);

  // Auto-fit the first time a diagram successfully draws this session.
  // viewport.ts's own one-shot guard — not this effect — is what makes every
  // later successful render (typing further valid edits) a no-op here; this
  // effect just needs to ask on every new svg, which is exactly what its
  // [svg] dependency does. useLayoutEffect so the fit is computed from the
  // just-painted DOM before the browser shows the unfit frame.
  useLayoutEffect(() => {
    if (svg === null) return;
    const sizes = currentSizes();
    if (sizes) viewportRef.current?.onRenderSuccess(sizes.content, sizes.container);
  }, [svg, currentSizes]);

  // Auto-fit whenever the pane itself resizes (splitter drag once #5 lands,
  // or a browser window resize) — always, regardless of onRenderSuccess's
  // one-shot history (SPEC.md §3, issue #7).
  useEffect(() => {
    const desk = deskRef.current;
    if (!desk) return;

    const observer = new ResizeObserver(() => {
      const sizes = currentSizes();
      if (sizes) viewportRef.current?.onContainerResize(sizes.content, sizes.container);
    });
    observer.observe(desk);
    return () => observer.disconnect();
  }, [currentSizes]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        fitNow();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [fitNow]);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  // One active pointer pans (same as before #8); two pinch. A third+ finger
  // is still recorded in the map (so its own eventual pointerup is handled
  // correctly) but produces no pan/zoom of its own, so it can't fight the
  // pinch calculation.
  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const pointers = activePointersRef.current;
    const previous = pointers.get(e.pointerId);
    if (!previous) return;
    const current = { x: e.clientX, y: e.clientY };
    pointers.set(e.pointerId, current);

    if (pointers.size === 1) {
      viewportRef.current?.pan(current.x - previous.x, current.y - previous.y);
      return;
    }
    if (pointers.size !== 2) return;

    const [idA, idB] = pointers.keys();
    if (idA === undefined || idB === undefined) return;
    const otherId = idA === e.pointerId ? idB : idA;
    const other = pointers.get(otherId);
    if (!other) return;

    const factor = pinchZoomFactor(previous, other, current, other);
    if (factor === null) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const mid = midpoint(current, other);
    viewportRef.current?.zoomBy(factor, { x: mid.x - rect.left, y: mid.y - rect.top });
  }

  function handlePointerEnd(e: React.PointerEvent<HTMLDivElement>) {
    activePointersRef.current.delete(e.pointerId);
    e.currentTarget.releasePointerCapture(e.pointerId);
  }

  // React's synthetic onWheel attaches its underlying listener as passive, so
  // preventDefault() silently fails there (https://github.com/facebook/react/issues/14856)
  // and native scroll/zoom fires alongside our own. A manually-attached,
  // explicitly non-passive listener is the only way to actually take over
  // the gesture.
  useEffect(() => {
    const el = deskRef.current;
    if (!el || !interactive) return;

    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const viewport = viewportRef.current;
      if (!viewport || !el) return;

      if (e.metaKey || e.ctrlKey) {
        const rect = el.getBoundingClientRect();
        viewport.zoomBy(Math.exp(-e.deltaY * 0.002), { x: e.clientX - rect.left, y: e.clientY - rect.top });
      } else {
        viewport.pan(-e.deltaX, -e.deltaY);
      }
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [interactive]);

  const percentage = Math.round(viewportState.scale * 100);
  // Keeps the words "diagram preview" as a substring either way, so a name
  // like Playwright's default (case-insensitive, substring) role-name match
  // for "Diagram preview" keeps working once a type is detected.
  const previewLabel = diagramType ? `${diagramType.label} diagram preview` : 'Diagram preview';

  return (
    // `grow`: same reasoning as Editor.tsx's own root section — a no-op in
    // the normal two-pane grid, required for this pane to fill the space
    // below App.tsx's tab strip in responsive tab mode (issue #37, M6.10).
    // `min-w-0` alongside `min-h-0`: same grid-item shrink trap Editor.tsx's
    // own section documents — this is the actual grid item (App.tsx's
    // wrapper is `display: contents`), and normal-flow content (the error
    // card's text) could otherwise refuse to shrink below its own intrinsic
    // width. The sheet itself is unaffected either way — it's absolutely
    // positioned, so it never contributes to this box's intrinsic size.
    <section aria-label="Preview" className="flex min-h-0 min-w-0 grow flex-col bg-ink">
      <div className="pane-label px-3 py-2.5 text-mute">Preview</div>
      <div className="relative m-3 mt-0 flex min-h-0 grow flex-col">
        <div
          ref={deskRef}
          role="img"
          aria-label={previewLabel}
          className="desk-grid relative min-h-0 grow touch-none overflow-hidden rounded-sm"
          onPointerDown={(e) => {
            if (onboarding.visible) onboarding.dismiss();
            if (interactive) handlePointerDown(e);
          }}
          onPointerMove={interactive ? handlePointerMove : undefined}
          onPointerUp={interactive ? handlePointerEnd : undefined}
          onPointerCancel={interactive ? handlePointerEnd : undefined}
        >
          {svg === null ? (
            <p className="absolute inset-0 m-auto max-w-xs px-6 text-center text-xs leading-relaxed text-text-paper/60">
              Nothing to draw yet. Pick a diagram type from New, or start typing.
            </p>
          ) : (
            <div
              ref={sheetRef}
              className={`absolute left-0 top-0 origin-top-left rounded-[3px] bg-sheet p-6 shadow-lg ${state === 'stale' ? 'opacity-[0.34]' : 'opacity-100'}`}
              style={{ transform: `translate(${viewportState.x}px, ${viewportState.y}px) scale(${viewportState.scale})` }}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          )}
        </div>

        {onboarding.visible && <OnboardingHint onDismiss={onboarding.dismiss} />}

        {/* A sibling of the role="img" desk, not a descendant — icon SVGs
            inside an img-role region are both an ARIA smell (an "image"
            shouldn't contain interactive buttons) and, concretely, would
            collide with e2e selectors that query for "the diagram's svg"
            within the preview region. */}
        {interactive && (
          <div
            className="absolute bottom-3 right-3 flex items-center gap-1 rounded-sm border border-ink-3 bg-ink-2/90 px-1 py-1 text-text-ink shadow-lg"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Zoom out"
              onClick={() => viewportRef.current?.zoomBy(1 / ZOOM_STEP, centerAnchor())}
              className="rounded-sm p-1.5 hover:bg-ink-3"
            >
              <Minus size={13} aria-hidden />
            </button>
            <span className="min-w-[3.5ch] text-center text-xs tabular-nums">{percentage}%</span>
            <button
              type="button"
              aria-label="Zoom in"
              onClick={() => viewportRef.current?.zoomBy(ZOOM_STEP, centerAnchor())}
              className="rounded-sm p-1.5 hover:bg-ink-3"
            >
              <Plus size={13} aria-hidden />
            </button>
            <button type="button" onClick={fitNow} className="rounded-sm px-2 py-1.5 text-xs hover:bg-ink-3">
              Fit
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mx-3 mb-3 border-l-4 border-pink bg-ink-2 px-3 py-2 text-xs">
          <p className="font-semibold text-pink">
            {error.line !== null ? `Line ${error.line} —` : "Couldn't draw this —"}
          </p>
          <pre className="mt-1 overflow-x-auto whitespace-pre-wrap font-mono text-[11px] text-mute">
            {error.message}
          </pre>
          {syntaxHint && (
            <p className="mt-1 text-[11px] text-mute">
              Hint: line <span className="font-mono text-text-ink">{syntaxHint.line}</span> has an unclosed quote.
            </p>
          )}
          {matchedViaAlias && diagramType && (
            <p className="mt-1 text-[11px] text-mute">
              This Mermaid version expects <code className="font-mono text-text-ink">{diagramType.keyword}</code>.
            </p>
          )}
          {diagramType && (
            <a
              href={diagramType.docsUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-[11px] text-pink underline"
            >
              {diagramType.label} docs
            </a>
          )}
        </div>
      )}
    </section>
  );
}
